import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { yieldToMainThread } from '@theme/utilities';

/**
 * Swaps the PDP between two linked products without a full page reload.
 *
 * Craftly splits size (Single/Double) and kit type (Candle/Ceramics) across
 * four separate products rather than variants, so switching is a product
 * navigation, not a variant change. Both toggles are the same interaction and
 * share this component.
 *
 * Progressive enhancement is the whole design: the markup is a list of plain
 * <a href> links to the other product, which work with JS disabled or broken.
 * This only intercepts the click and morphs the section in place.
 *
 * @extends {Component}
 */
export default class CraftlyProductSwitcher extends Component {
  /** Section to re-render. Product template's main section is `main`. */
  get sectionId() {
    return this.dataset.sectionId || 'main';
  }

  /**
   * Whether switching should keep the visitor on the current page.
   *
   * On the PDP the destination *is* a page, so the swap doubles as a
   * navigation and the URL should follow. On the homepage there is no page to
   * move to — the featured section re-renders itself in place instead. Either
   * way the section is fetched from the target product's own URL: Liquid has
   * no way to read an arbitrary query parameter, but the Section Rendering
   * API renders a section "in the context of" whatever page URL it's
   * requested against, so fetching `/products/<handle>?section_id=...` gives
   * back the section with that product's data regardless of which page the
   * visitor is actually looking at. Set by the section via
   * `data-switch-mode="in-place"`.
   *
   * @returns {boolean}
   */
  get isInPlace() {
    return this.dataset.switchMode === 'in-place';
  }

  /**
   * Builds the URL to render the section from — the target product's own
   * page. In-place mode still fetches from there; it only skips pushing that
   * URL into the visible address bar (see `#navigate`'s `updateHistory`).
   *
   * @param {HTMLAnchorElement} link
   * @returns {URL} Destination.
   */
  #destinationFor(link) {
    return new URL(link.href, window.location.href);
  }

  connectedCallback() {
    super.connectedCallback();

    // Restore the correct product when the user hits back/forward, otherwise
    // the URL and the rendered section drift apart.
    window.addEventListener('popstate', this.#onPopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#onPopState);
    this.#abortController?.abort();
  }

  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * Handles a click on one of the toggle links.
   * Bound in Liquid via `on:click="/select"`.
   * @param {MouseEvent} event
   */
  select(event) {
    // `event.target` is normalized by the framework's event delegation to
    // always be the element carrying `on:click` (this switcher itself), not
    // the node actually clicked — see registerEventListeners in component.js.
    // The real target must come from composedPath().
    const clicked = /** @type {HTMLElement | undefined} */ (event.composedPath?.()[0]);
    const link = clicked?.closest('a[href]');

    if (!(link instanceof HTMLAnchorElement)) return;
    if (!this.contains(link)) return;

    // Let the browser handle anything that isn't a plain left click, so
    // cmd/ctrl-click, middle click and "open in new tab" keep working.
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;

    // Already the current product — nothing to swap.
    if (link.dataset.selected === 'true') return;

    const destination = this.#destinationFor(link);

    // Cross-origin or otherwise unexpected: fall back to normal navigation.
    if (destination.origin !== window.location.origin) return;

    event.preventDefault();

    // In-place swaps are not navigations — the visitor stays on the current
    // page even though the section is fetched from the target product's URL.
    // Swap without touching history.
    this.#navigate(destination, {
      updateHistory: !this.isInPlace,
      selectedHref: link.getAttribute('href') ?? undefined,
      fallbackUrl: link.href,
    });
  }

  /**
   * Renders the destination product into the current section.
   * @param {URL} destination
   * @param {object} [options]
   * @param {boolean} [options.updateHistory]
   * @param {string} [options.selectedHref] Href of the clicked option.
   * @param {string} [options.fallbackUrl] Where to go if rendering fails.
   */
  async #navigate(destination, { updateHistory = true, selectedHref, fallbackUrl } = {}) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.setAttribute('aria-busy', 'true');

    // Push history before the fetch so a slow network doesn't leave the URL
    // lagging behind what the user clicked.
    if (updateHistory && destination.href !== window.location.href) {
      history.pushState({ craftlyProductSwitch: true }, '', destination.href);
    }

    try {
      await sectionRenderer.renderSection(this.sectionId, {
        url: destination,
        cache: false,
      });
    } catch (error) {
      if (this.#abortController?.signal.aborted) return;

      // Section rendering failed — fall back to a real navigation rather than
      // stranding the user on a URL whose content never arrived.
      console.error('[craftly] product switch failed, falling back', error);
      window.location.assign(fallbackUrl ?? destination.href);
      return;
    } finally {
      this.removeAttribute('aria-busy');
    }

    await yieldToMainThread();
    this.#restoreFocus(selectedHref);
  }

  /**
   * Restores focus after a swap. The clicked control is replaced by the morph,
   * so without this focus falls back to <body> and screen reader users lose
   * their place.
   *
   * On the PDP the swap is a navigation, so the product title is the right
   * landing point. In-place it is not: the visitor is part-way through a set of
   * pickers and has not gone anywhere, so focus returns to the toggle option
   * they just chose — matching how a radio group behaves.
   *
   * @param {string} [selectedHref] Href of the option that was clicked.
   */
  #restoreFocus(selectedHref) {
    const section = document.querySelector(`#shopify-section-${this.sectionId}`);

    if (!section) return;

    if (this.isInPlace) {
      // The re-rendered markup marks the now-current option as selected.
      // Match on the href by reading attributes rather than building a
      // selector: an href contains `/` and `?`, which are not valid unescaped
      // in an attribute selector, and CSS.escape is for identifiers, not values.
      const byHref = selectedHref
        ? [...section.querySelectorAll('a[href]')].find((a) => a.getAttribute('href') === selectedHref)
        : null;

      const target = byHref ?? section.querySelector('a[data-selected="true"]');

      if (target instanceof HTMLElement) {
        target.focus({ preventScroll: true });
        return;
      }
    }

    const heading = section.querySelector('h1');

    if (!(heading instanceof HTMLElement)) return;

    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
  }

  /**
   * Class-field arrow so the listener stays bound and removable (Horizon's
   * convention for auto-bound handlers, cf. Component#isDescendant).
   * @param {PopStateEvent} event
   */
  #onPopState = (event) => {
    // Only handle entries this component pushed. Other components (the variant
    // picker, facets) push their own, and re-rendering on those would fight them.
    if (!event.state?.craftlyProductSwitch) return;

    this.#navigate(new URL(window.location.href), { updateHistory: false });
  };
}

if (!customElements.get('craftly-product-switcher')) {
  customElements.define('craftly-product-switcher', CraftlyProductSwitcher);
}
